import { useMemo } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { transition, variants } from './variants';
import { getLocalTimezones } from '../../../../lib/timezones';
import styles from './ScheduleForm.module.css';

const DeviceSelect = (props) => {
    const { schedule, handleSetSchedule } = props;

    const deviceTypes = [{
        type: 'ios',
        label: 'iOS',
        icon: 'fa-brands fa-apple'
    },
    {
        type: 'android',
        label: 'Android',
        icon: 'fa-brands fa-android'
    },
    {
        type: 'desktop',
        label: 'Desktop',
        icon: 'fa-regular fa-computer'
    }];

    
    if (schedule?.device?.enabled !== true) {
        return <></>;
    };

    return (
        <div className={styles.deviceContainer}>
            <p className={styles.deviceLabel}>Select Device</p>
            <select
                className={styles.deviceSelect}
                value={schedule.device?.value}
                onChange={e => handleSetSchedule({ type: 'device', dataType: 'value', value: e.target.value })}
            >
                <option default value='none' >None</option>
                {deviceTypes.map(opt => (
                    <option
                        key={opt.type}
                        value={opt.type}
                        className={styles.deviceSelectOption}
                    >
                        {opt.label}
                    </option>
                ))}
            </select>
        </div>
    );
};

const DaySelect = (props) => {
    const { schedule, handleSetSchedule } = props;

    const days = [{
        id: '0',
        day: 'Sunday',
        label: 'Sun',
    },
    {
        id: '1',
        day: 'Monday',
        label: 'Mon',
    },
    {
        id: '2',
        day: 'Tuesday',
        label: 'Tue',
    },
    {
        id: '3',
        day: 'Wednesday',
        label: 'Wed',
    },
    {
        id: '4',
        day: 'Thursday',
        label: 'Thu',
    },
    {
        id: '5',
        day: 'Friday',
        label: 'Fri',
    },
    {
        id: '6',
        day: 'Saturday',
        label: 'Sat',
    }];

    const handleDays = (checked, id) => {
        const updatedDays = checked ? schedule.day.value.concat(id) : schedule.day.value.filter(d => d !== id);
        handleSetSchedule({
            type: 'day',
            dataType: 'value',
            value: updatedDays.sort((a, b) => Number(a) - Number(b))
        });
    };

    return (
        <div className={styles.dayContainer}> 
            <p className={styles.daysLabel}>Select Day(s)</p>
            <div className={styles.days}>
                {days.map(day => (
                    <label key={day.id} className={styles.day}>
                        <input
                            id={`day-option-${day.id}`}
                            type='checkbox'
                            className={styles.dayInput}
                            checked={schedule.day.value.includes(day.id) ? true : false}
                            onChange={e => handleDays(e.target.checked, day.id)}
                        />
                        {day.label}
                    </label>
                ))}
            </div>
        </div>
    );
};

const TimeSelect = (props) => {
    const { schedule, handleSetSchedule } = props;
    const timezones = getLocalTimezones();

    return (
        <div className={styles.timeContainer}>
            <div className={styles.timeInputGroup}>
                <label htmlFor='time-start-input' className={styles.timeLabel}>Start Time</label>
                <input
                    className={styles.timeInput}
                    id='time-start-input'
                    type='time'
                    value={schedule.time.value.start}
                    onChange={e => handleSetSchedule({ type: 'time', dataType: 'value', value: { ...schedule.time.value, start: e.target.value } })}
                />
            </div>
            <div className={styles.timeInputGroup}>
                <label htmlFor='time-end-input' className={styles.timeLabel}>End Time</label>
                <input
                    className={styles.timeInput}
                    id='time-end-input'
                    type='time'
                    value={schedule.time.value.end}
                    onChange={e => handleSetSchedule({ type: 'time', dataType: 'value', value: { ...schedule.time.value, end: e.target.value } })}
                />
            </div>
            <div className={styles.timeInputGroup}>
                <label htmlFor='time-timezone-select' className={styles.timeLabel}>Timezone</label>
                <select
                    value={schedule.time.value.timezone}
                    id='time-timezone-select'
                    onChange={e => handleSetSchedule({ type: 'time', dataType: 'value', value: { ...schedule.time.value, timezone: e.target.value }})}
                    className={styles.timezoneSelect}
                >
                    <option default disabled value='' >Select Timezone</option>
                    <option value='device-tz'>Device Timezone</option>
                    {timezones.map((tz) => (
                        <option key={tz.name} value={tz.name}>
                            {tz.offset} {tz.name}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
};

export default function ScheduleForm(props) {
    const { schedule, error, handleSetSchedule } = props;

    const erroredSections = useMemo(() => {
        const sections = {
            day: false,
            device: false,
            time: false,
        };

        if (error.validityResults.length > 0) {
            error.validityResults.forEach(err => {
                if (schedule.id === err.id) {
                    if (schedule.device.enabled && err.type === 'invalid-device') {
                        sections.device = true;
                    };

                    if (schedule.day.enabled && err.type === 'invalid-day') {
                        sections.day = true;
                    };

                    if (schedule.time.enabled) {
                        if (err.type === 'invalid-time' || err.type === 'invalid-range') {
                            sections.time = true;
                        };
                    };
                };
            });
        };

        return sections;
    },[schedule, error.validityResults]);

    return (
        <div className={styles.container}>
            <div>

                <div className={styles.selectorsLabel}>The scan destination is based on...</div>
                <div className={styles.selectors}>
                    <label className={styles.scheduleLabel}>
                        <input
                            id='schedule-device-input'
                            name='device-enabled'
                            type='checkbox'
                            checked={schedule.device.enabled}
                            className={styles.scheduleInput}
                            onChange={e => handleSetSchedule({ type: 'device', dataType: 'enabled', value: e.target.checked })}
                        />
                        <i className={schedule.device.enabled ? 'far fa-square-check' : 'far fa-square'} />
                        Device
                    </label>
                    <label className={styles.scheduleLabel}>
                        <input
                            id='schedule-day-input'
                            name='day-enabled'
                            type='checkbox'
                            checked={schedule.day.enabled}
                            className={styles.scheduleInput}
                            onChange={e => handleSetSchedule({ type: 'day', dataType: 'enabled', value: e.target.checked })}
                        />
                        <i className={schedule.day.enabled ? 'far fa-square-check' : 'far fa-square'} />
                        Day
                    </label>
                    <label className={styles.scheduleLabel}>
                        <input
                            id='schedule-time-input'
                            name='time-enabled'
                            type='checkbox'
                            checked={schedule.time.enabled}
                            className={`${styles.scheduleInput} ${schedule.time.enabled && styles.enabled}`}
                            onChange={e => handleSetSchedule({ type: 'time', dataType: 'enabled', value: e.target.checked })}
                        />
                        <i className={schedule.time.enabled ? 'far fa-square-check' : 'far fa-square'} />
                        Time
                    </label>
                </div>
            </div>
            <AnimatePresence initial={false}>
                {schedule.device.enabled &&
                    <motion.div
                        key='device'
                        transition={transition}
                        variants={variants}
                        initial='initial'
                        animate='animate'
                        exit='initial'
                        className={`${styles.deviceWrap} ${erroredSections.device && styles.errorWrap}`}
                    >
                        <DeviceSelect schedule={schedule} handleSetSchedule={handleSetSchedule} />
                    </motion.div>
                }
                {schedule.day.enabled &&
                    <motion.div
                        key='day'
                        transition={transition}
                        variants={variants}
                        initial='initial'
                        animate='animate'
                        exit='initial'
                        className={`${styles.dayWrap} ${erroredSections.day && styles.errorWrap}`}
                    >
                        <DaySelect schedule={schedule} handleSetSchedule={handleSetSchedule} />
                    </motion.div>
                }
                {schedule.time.enabled &&
                    <motion.div
                        key='time'
                        transition={transition}
                        variants={variants}
                        initial='initial'
                        animate='animate'
                        exit='initial'
                        className={`${styles.timeWrap} ${erroredSections.time && styles.errorWrap}`}
                    >
                        <TimeSelect schedule={schedule} handleSetSchedule={handleSetSchedule} />
                    </motion.div>
                }
            </AnimatePresence>
        </div>
    );
};
